import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { sturgesBinCount, binValues } from '../../utils/numericBinning';
import ResponseDrillTable, { toDrillRows } from './ResponseDrillTable';

// The answers this chart can plot, still carrying who gave them. Answers that
// are not numbers are dropped HERE rather than downstream, so the list handed
// to the binner and the list the drill-down reads back are the same list in the
// same order — a non-numeric answer in the middle must not shift every
// respondent one bin to the left.
export function numericAnswerRows(rows = []) {
  return rows
    .map(row => ({ ...row, number: Number(row.value) }))
    .filter(row => !isNaN(row.number));
}

// binValues returns one bin label per number it was given, positionally.
export function rowsInBin(answerRows = [], assignedBins = [], label) {
  return answerRows.filter((_, index) => assignedBins[index] === label);
}

const mean = (arr) => {
  if (arr.length === 0) return NaN;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
};

const median = (arr) => {
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
};

const sd = (arr) => {
  if (arr.length === 0) return NaN;
  const m = mean(arr);
  const variance = arr.reduce((acc, val) => acc + Math.pow(val - m, 2), 0) / arr.length;
  return Math.sqrt(variance);
};

const formatNumber = (num) => {
  if (Number.isInteger(num)) return num;
  return num.toFixed(2);
};

export default function ScalarCanvas({ values, binCount, view = 'histogram', chartRef, rows = [], onOpenSession }) {
  // Which bin the author clicked, by its label. Held above the empty-state
  // return so the hook order never depends on whether answers arrived.
  const [drillBin, setDrillBin] = useState(null);

  // Answers keep their respondent when the caller supplies rows; a caller that
  // passes only values still gets its chart, minus the drill-down.
  const answerRows = numericAnswerRows(rows.length ? rows : values.map(value => ({ value })));
  const nums = answerRows.map(row => row.number);
  const canDrill = rows.length > 0;

  if (nums.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 300,
        color: '#5F6368',
        fontFamily: 'var(--font-family-primary)'
      }}>
        No numeric responses yet.
      </div>
    );
  }

  const bc = binCount || sturgesBinCount(nums.length);
  const { binnedLabels, assignedBins } = binValues(nums, bc);
  const drillRows = drillBin == null ? [] : toDrillRows(rowsInBin(answerRows, assignedBins, drillBin));

  const countByLabel = {};
  assignedBins.forEach(l => {
    if (l != null) countByLabel[l] = (countByLabel[l] || 0) + 1;
  });

  const histData = binnedLabels.map(label => ({
    label,
    count: countByLabel[label] || 0
  }));

  const stats = {
    n: nums.length,
    mean: mean(nums),
    median: median(nums),
    sd: sd(nums),
    min: Math.min(...nums),
    max: Math.max(...nums)
  };

  const statItems = [
    { label: 'n', value: stats.n },
    { label: 'mean', value: formatNumber(stats.mean) },
    { label: 'median', value: formatNumber(stats.median) },
    { label: 'σ', value: formatNumber(stats.sd) },
    { label: 'min', value: formatNumber(stats.min) },
    { label: 'max', value: formatNumber(stats.max) }
  ];

  const renderSummaryStrip = () => (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      marginBottom: '16px',
      fontFamily: 'var(--font-family-primary)'
    }}>
      {statItems.map((item, index) => (
        <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: '11px', color: '#5F6368' }}>{item.label}</span>
          <span style={{ fontSize: '13px', color: '#202124', fontWeight: 600 }}>{item.value}</span>
        </div>
      ))}
    </div>
  );

  // A second click on the bar already open closes it, so the bar is a toggle
  // rather than a trap the author has to hunt for the × to escape.
  const handleBarClick = (entry) => {
    if (!canDrill) return;
    const label = entry?.label ?? entry?.payload?.label ?? null;
    setDrillBin(current => (current === label ? null : label));
  };

  const renderHistogram = () => (
    <div style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={histData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <XAxis
            dataKey="label"
            tick={{ fill: '#5F6368', fontSize: 12 }}
          />
          <YAxis
            tick={{ fill: '#5F6368', fontSize: 12 }}
          />
          <Tooltip
            contentStyle={{ fontFamily: 'var(--font-family-primary)' }}
          />
          <Bar dataKey="count" fill="#5B9FFF" onClick={handleBarClick} cursor={canDrill ? 'pointer' : undefined} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderHorizontalBars = () => (
    <div style={{ width: '100%', height: Math.max(300, histData.length * 42) }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart layout="vertical" data={histData} margin={{ top: 5, right: 30, left: 24, bottom: 5 }}>
          <XAxis
            type="number"
            allowDecimals={false}
            tick={{ fill: '#5F6368', fontSize: 12 }}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={120}
            tick={{ fill: '#5F6368', fontSize: 12 }}
          />
          <Tooltip contentStyle={{ fontFamily: 'var(--font-family-primary)' }} />
          <Bar dataKey="count" name="Response count" fill="#5B9FFF" onClick={handleBarClick} cursor={canDrill ? 'pointer' : undefined} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  return (
    <div style={{ fontFamily: 'var(--font-family-primary)', color: '#202124' }}>
      {renderSummaryStrip()}
      {/* The drill-down sits outside chartRef: it is a way into the answers,
          not part of the picture the author exports as a PNG. */}
      <div ref={chartRef}>
        {view === 'horizontal-bars' ? renderHorizontalBars() : renderHistogram()}
      </div>
      {canDrill && (
        <div style={{ fontSize: 12, color: '#5F6368', marginTop: 8 }}>Click a bar to see its answers.</div>
      )}
      {drillBin != null && (
        <ResponseDrillTable
          label={drillBin}
          rows={drillRows}
          onClose={() => setDrillBin(null)}
          onOpenSession={onOpenSession}
        />
      )}
    </div>
  );
}
